import React, { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { subscriptionApi } from '../lib/subscriptionApi';
import { supabase } from '../lib/supabase';

const SuccessPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const { refreshSubscription } = useSubscription();
  const [isProcessing, setIsProcessing] = useState(true);
  const [error, setError] = useState('');
  const [isRefreshingSession, setIsRefreshingSession] = useState(false);

  useEffect(() => {
    const handleSuccess = async () => {
      try {
        const sessionId = searchParams.get('session_id');
        const success = searchParams.get('success');
        const userId = searchParams.get('user_id');

        console.log('Success page params:', { sessionId, success, userId, user: user?.id });

        if (success === 'true' && sessionId) {
          // If user is not authenticated, try to refresh the session first
          if (!user && !loading) {
            console.log('User not authenticated, attempting to refresh session...');
            setIsRefreshingSession(true);
            
            try {
              const { data: { session }, error: refreshError } = await supabase.auth.getSession();
              if (refreshError) {
                console.error('Session refresh error:', refreshError);
              } else if (session?.user) {
                console.log('Session refreshed successfully:', session.user.id);
                // The AuthContext will update automatically, so we can continue
                return; // Exit early, useEffect will run again with the new user
              }
            } catch (refreshError) {
              console.error('Failed to refresh session:', refreshError);
            }
            
            setIsRefreshingSession(false);
            
            // If still not authenticated, redirect to login with helpful message
            console.log('Still not authenticated after refresh, redirecting to login');
            navigate('/login?message=Please sign in to complete your subscription&returnUrl=' + encodeURIComponent(window.location.href));
            return;
          }

          // Wait for authentication to be ready
          if (loading || isRefreshingSession) {
            console.log('Waiting for authentication to be ready...');
            return;
          }

          console.log('Processing successful payment for user:', user?.id);
          
          // Handle the successful payment
          const result = await subscriptionApi.handleSuccessfulPayment(sessionId);
          
          if (result.success) {
            console.log('Payment processed successfully');
            await refreshSubscription();
            
            // Redirect to dashboard after a short delay
            setTimeout(() => {
              navigate('/dashboard?success=true', { replace: true });
            }, 2000);
          } else {
            console.error('Failed to process payment:', result.error);
            setError(result.error || 'Failed to process payment');
          }
        } else {
          // No success parameters, redirect to dashboard
          navigate('/dashboard', { replace: true });
        }
      } catch (error: any) {
        console.error('Error handling success:', error);
        setError(error.message || 'An error occurred while processing your payment');
      } finally {
        setIsProcessing(false);
      }
    };

    handleSuccess();
  }, [searchParams, user, loading, isRefreshingSession, navigate, refreshSubscription]);

  // Show loading state while refreshing session
  if (loading || isRefreshingSession) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            {isRefreshingSession ? 'Refreshing your session...' : 'Loading...'}
          </h2>
          <p className="text-gray-600">
            {isRefreshingSession 
              ? 'Please wait while we restore your session to complete your subscription.'
              : 'Please wait while we process your payment.'
            }
          </p>
        </div>
      </div>
    );
  }

  if (isProcessing) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Processing your payment...
          </h2>
          <p className="text-gray-600">
            Please wait while we complete your subscription setup.
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">
            Payment Processing Error
          </h2>
          <p className="text-gray-600 mb-6">
            {error}
          </p>
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full bg-primary text-white py-2 px-4 rounded-lg hover:bg-primary/90 transition-colors"
          >
            Go to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Payment Successful!
        </h2>
        <p className="text-gray-600 mb-6">
          Your subscription has been activated successfully. You will be redirected to your dashboard shortly.
        </p>
        <div className="animate-pulse">
          <div className="h-2 bg-gray-200 rounded mb-2"></div>
          <div className="h-2 bg-gray-200 rounded w-3/4 mx-auto"></div>
        </div>
      </div>
    </div>
  );
};

export default SuccessPage; 